import {
  useStripe,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import type {
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
} from '@stripe/stripe-js/types/stripe-js/payment-request';
import logEvent from 'analytics/logEvent';
import { logProcessingFailed } from 'domains/payments/processingFailed';
import React, { useEffect, useState } from 'react';
import cn from 'classnames';

import { getTariff } from 'domains/products/products.utils';

import { CircularProgress } from 'components/basic/Loaders';
import type { LogPaymentArgsType } from 'components/complex/payments/PaymentForm/index';
import PaymentPreviewConfirm from 'components/complex/payments/PaymentForm/PaymentPreviewConfirm';

import { useAppDispatch, useAppSelector } from 'redux-store';
import type { ProductType } from 'redux-store/slices/products/types';
import {
  chargeFinish,
  setChargeProcessingStatus,
} from 'redux-store/slices/purchases';
import attachPaymentsMethodThunk from 'redux-store/thunks/paymentsMethods/attachPaymentsMethodThunk';
import chargePurchaseThunk from 'redux-store/thunks/purchases/chargePurchaseThunk';
import stripeInvoicePreviewThunk from 'redux-store/thunks/payments/stripeInvoicePreviewThunk';
// import { resetPreview } from 'redux-store/slices/paymentsPreview';

import s from './PaymentForm.module.scss';

interface Props {
  selectedPlan: ProductType;
  coupon?: string;
  logClickButton: (arg: LogPaymentArgsType) => void;
}

export default function PaymentButtons({
  selectedPlan,
  coupon,
  logClickButton,
}: Props): JSX.Element | null {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null,
  );
  const [isLoading, setLoading] = useState(true);
  // const [previewRequested, setPreviewRequested] = useState(false);

  // const [pendingPaymentMethodId, setPendingPaymentMethodId] = useState<
  //   string | null
  // >(null);
  // const [pendingComplete, setPendingComplete] = useState<
  //   PaymentRequestPaymentMethodEvent['complete'] | null
  // >(null);

  const dispatch = useAppDispatch();
  // const paymentsPreview = useAppSelector(state => state.paymentsPreview);
  const region = useAppSelector(state => state.ui.region);
  const [isNeedToSendAnalyticsEvent, setIsNeedToSendAnalyticsEvent] =
    useState(true);
  const [availableStore, setAvailableStore] = useState<string | undefined>();

  // useEffect(() => {
  //   if (previewRequested && paymentsPreview.status === 'ready') {
  //     logEvent('Web_Payment_Tax_Preview_Shown');
  //   }
  // }, [previewRequested, paymentsPreview.status]);

  useEffect(() => {
    const { currency, price, discount_price } = selectedPlan.prices[0];
    const { title } = selectedPlan;

    const currentPrice = discount_price ?? price;

    if (stripe) {
      const isNeedRequestTax = region === 'US';
      const pr = stripe.paymentRequest({
        country: 'US',
        currency,
        total: {
          label: title,
          amount: +(+currentPrice * 100).toFixed(2),
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestShipping: isNeedRequestTax,
        // requestShipping: false,
      });

      pr.on('cancel', () => {
        logEvent('Web_Payment_Confirm_Popup_Closed');
      });

      pr.on('shippingaddresschange', async (event) => {
        try {
          const shippingAddress = event.shippingAddress;

          if (shippingAddress.country !== 'US' || !shippingAddress.city || !shippingAddress.region || !shippingAddress.postalCode) {
            return event.updateWith({
              status: 'fail',
              shippingOptions: [{ id: 'free', label: 'Free', detail: '', amount: 0 }],
            });
          }

          const address = {
            line1: shippingAddress.addressLine?.[0] ?? null,
            city: shippingAddress.city,
            state: shippingAddress.region,
            postal_code: shippingAddress.postalCode,
            country: shippingAddress.country,
          };

          const price_ids = selectedPlan.prices.map(
            price_id => price_id.stripe_price_id,
          );

          logEvent('Web_Payment_Tax_Preview_Requested');

          const preview = await dispatch(
            stripeInvoicePreviewThunk({ price_ids, coupon, ...address })
          ).unwrap();

          if (preview.success) {
            const previewResult = preview.result;
            return event.updateWith({
              status: 'success',
              shippingOptions: [{ id: 'free', label: 'Free', detail: '', amount: 0 }],
              total: { label: title, amount: Math.round(Number(previewResult.total) * 100) },
              displayItems: [
                { label: 'Subtotal', amount: Math.round(Number(previewResult.subtotal) * 100) },
                { label: 'Tax', amount: Math.round(Number(previewResult.tax) * 100) },
              ],
            });
          }
          else {
            return event.updateWith({
              status: 'fail',
              shippingOptions: [{ id: 'free', label: 'Free', detail: '', amount: 0 }],
            });
          }
          
        }
        catch (e) {
          return event.updateWith({
            status: 'fail',
            shippingOptions: [{ id: 'free', label: 'Free', detail: '', amount: 0 }],
          });
          // event.updateWith({ status: 'fail' });
        }
      });

      pr.on('paymentmethod', async e => {
        let payment_method_id = null;

        const attachResult = await dispatch(
          attachPaymentsMethodThunk({ id: e.paymentMethod.id }),
        ).unwrap();

        if (attachResult.success) {
          payment_method_id = attachResult.result.id;
          logEvent('Web_Payment_New_Card_Attached');
        } 
        else {
          dispatch(
            setChargeProcessingStatus({
              status: 'fulfilled',
              details: 'some_error',
              // detail: attachResult.result.code,
            }),
          );
          logProcessingFailed({
            code: attachResult?.result?.code,
            declineCode: attachResult?.result?.decline_code,
            type: attachResult?.result?.type,
            message: attachResult?.result?.message,
          });

          return;
        }

        if (!payment_method_id) {
          e.complete('fail');
          return;
        }

        const price_ids = selectedPlan.prices.map(
          price_id => price_id.stripe_price_id,
        );
        // const billingAddress = e.paymentMethod.billing_details?.address;

        // if (region === 'US' && billingAddress) {
        //   logEvent('Web_Payment_Tax_Preview_Requested');
        //   setPreviewRequested(true);
        //   setPendingPaymentMethodId(payment_method_id);
        //   setPendingComplete(() => e.complete);

        //   await dispatch(
        //     stripeInvoicePreviewThunk({
        //       price_ids,
        //       payment_method_id,
        //       coupon,
        //       ...billingAddress
        //     }),
        //   );

        //   return;
        // }

        let shippingAddress;
        if (isNeedRequestTax && e.shippingAddress && 
          (e.shippingAddress.city && e.shippingAddress.country && e.shippingAddress.region && e.shippingAddress.postalCode)
        ) {
          shippingAddress = {
            city: e.shippingAddress.city,
            state: e.shippingAddress.region,
            postal_code: e.shippingAddress.postalCode,
            country: e.shippingAddress.country,
          }
        }

        const result = await dispatch(
          chargePurchaseThunk({
            coupon,
            product_id: selectedPlan.id,
            payment_method_id,
            price_ids: price_ids,
            billing_address: shippingAddress,
            extra: {
              interval: selectedPlan.prices[0].interval,
              tariff: getTariff(selectedPlan),
              discount: Number(selectedPlan.prices[0].discount),
              price: selectedPlan.prices[0].discount_price ?? selectedPlan.prices[0].price,
            },
          }),
        ).unwrap();

        await dispatch(chargeFinish(result));

        e.complete('success');
      });

      // Check the availability of the Payment Request API.
      pr.canMakePayment().then(result => {
        if (result) {
          //to force re-render payment button set payment request to null
          setPaymentRequest(null);
          setPaymentRequest(pr);

          if (isNeedToSendAnalyticsEvent) {
            const _availableStore = Object.entries(result).find(el => el[1]);

            if (_availableStore) {
              setAvailableStore(_availableStore[0]);
              logEvent('Web_Payment_System_Button_Viewed', {
                payment_system: _availableStore[0],
              });
            }

            setIsNeedToSendAnalyticsEvent(false);
          }
        }

        setLoading(false);
      });
    }
  }, [isNeedToSendAnalyticsEvent, stripe, selectedPlan, coupon, dispatch]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isLoading) {
      timeoutId = setTimeout(() => {
        logEvent('Web_Payment_Pay_Button_Error');
      }, 10000);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoading]);

  if (paymentRequest) {
    return (
      <div className={cn(s.paymentButtonsWrapper)}>
        <PaymentRequestButtonElement
          className={s.stripePaymentButton}
          onClick={(e): void => {

            if (availableStore?.length) {
              logClickButton({ paymentSystem: availableStore });
            }
          }}
          options={{
            paymentRequest,
            style: {
              paymentRequestButton: {
                type: 'default',
                theme: 'light',
                height: '48px',
              },
            },
          }}
        />
        {/* {previewRequested && (
          <PaymentPreviewConfirm
            status={paymentsPreview.status}
            subtotal={paymentsPreview.subtotal}
            tax={paymentsPreview.tax}
            total={paymentsPreview.total}
            currency={paymentsPreview.currency}
            error={paymentsPreview.error}
          />
        )} */}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={s.paymentButtonsWrapper}>
        <CircularProgress />
      </div>
    );
  }

  return null;
}
